import React, { useMemo, useEffect } from 'react';
import { useAiRovoCurrentRouteConfigurationStoreActions } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router/src/controllers/router-store';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';
import { CONVERSATION_ASSISTANT_CONTAINER_ID } from '@atlassian/conversation-assistant/utils';
import { useActiveRightSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import {
	jiraRouteToConfigProvider,
	type RovoEntryPointComponent,
} from './jiraRouteToConfigProvider.tsx';

const useCurrentRouteName = createRouterSelector((state) => state.route.name);

const entryPointParams = {};

export const AiRovoCurrentRouteConfigurationProvider = (): React.JSX.Element | null => {
	const routeName = useCurrentRouteName();
	const { resetConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();

	const configuration = useMemo(() => {
		if (routeName in jiraRouteToConfigProvider && jiraRouteToConfigProvider[routeName]) {
			return jiraRouteToConfigProvider[routeName];
		}
		return null;
	}, [routeName]);

	useEffect(() => {
		if (!configuration?.entrypoint) {
			resetConfiguration();
		}
	}, [resetConfiguration, configuration]);

	if (!configuration) {
		return null;
	}

	return (
		<>
			<RovoEntryPointContainer entrypoint={configuration.entrypoint} />
			{configuration.nudges && configuration.nudges()}
		</>
	);
};

const RovoEntryPointContainer = ({ entrypoint }: { entrypoint: RovoEntryPointComponent }) => {
	// eslint-disable-next-line @atlassian/react-entrypoint/prefer-entrypoint-file-import
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		entrypoint,
		entryPointParams,
	);
	const { setConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();
	const runtimeProps = useMemo(() => ({ setConfiguration }), [setConfiguration]);
	const { mode } = useRovoExperienceAPIStore();

	// TODO this shouldn't be needed since mode should be set to 'sidebar' when the sidebar is open,
	// but it isn't working right now. We should fix this in the rovo experience api store.
	const { panelId, isCollapsed } = useActiveRightSidebarState() || {};
	const isActive = panelId === CONVERSATION_ASSISTANT_CONTAINER_ID && !isCollapsed;

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (mode === 'mini-modal' || mode === 'sidebar' || isActive) {
			entryPointActions.load();
		}
	}, [entryPointActions, mode, isActive]);

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="jwm.rovo.current-route-configuration"
			packageName="jiraAiRovoActionsRouteProvider"
			runtimeProps={runtimeProps}
		/>
	);
};
